/**
 * AudienceBuilder - Component for building campaign audience criteria
 *
 * Features:
 * - Audience type selector (all/plans/tags/custom)
 * - Plan multi-select with subscriber counts
 * - Tag multi-select
 * - Estimated audience count preview
 */
import { useState, useEffect } from 'react';
import { Users, CreditCard, Tag, Filter, Check } from 'lucide-react';
import {
  Label,
  RadioGroup,
  RadioGroupItem,
  Checkbox,
  cn,
} from '@respectly/ui';
import type { CampaignAudienceType } from '@respectly/shared-types';
import { usePlansStore } from '../../stores/usePlansStore';
import { useTagsStore } from '../../stores/useTagsStore';

// ============================================
// Types
// ============================================

interface AudienceBuilderProps {
  audienceType: CampaignAudienceType;
  audiencePlans?: string[];
  audienceTags?: string[];
  audienceFilter?: string;
  onChange: (
    type: CampaignAudienceType,
    plans?: string[],
    tags?: string[],
    filter?: string
  ) => void;
  className?: string;
}

interface AudienceOption {
  value: CampaignAudienceType;
  label: string;
  description: string;
  icon: typeof Users;
}

// ============================================
// Configuration
// ============================================

const audienceOptions: AudienceOption[] = [
  {
    value: 'all',
    label: 'All Accounts',
    description: 'Target all active tenant accounts',
    icon: Users,
  },
  {
    value: 'plans',
    label: 'By Plan',
    description: 'Target accounts on specific plans',
    icon: CreditCard,
  },
  {
    value: 'tags',
    label: 'By Tags',
    description: 'Target accounts with specific tags',
    icon: Tag,
  },
  {
    value: 'custom',
    label: 'Custom Filter',
    description: 'Build custom targeting criteria',
    icon: Filter,
  },
];

// ============================================
// Component
// ============================================

export function AudienceBuilder({
  audienceType,
  audiencePlans = [],
  audienceTags = [],
  audienceFilter,
  onChange,
  className,
}: AudienceBuilderProps) {
  const [selectedPlans, setSelectedPlans] = useState<string[]>(audiencePlans);
  const [selectedTags, setSelectedTags] = useState<string[]>(audienceTags);

  // Load plans and tags
  const { plans, loadPlans, isLoadingList: isLoadingPlans } = usePlansStore();
  const { tags, loadTags, isLoadingList: isLoadingTags } = useTagsStore();

  // Always load plans on mount for audience estimation (needed for "All Accounts" count too)
  const plansCount = plans?.length ?? 0;
  useEffect(() => {
    if (plansCount === 0) {
      loadPlans();
    }
  }, [plansCount, loadPlans]);

  useEffect(() => {
    if (audienceType === 'tags' && tags.length === 0) {
      loadTags();
    }
  }, [audienceType, tags.length, loadTags]);

  // Handle audience type change
  const handleTypeChange = (newType: CampaignAudienceType) => {
    onChange(newType, selectedPlans, selectedTags, audienceFilter);
  };

  // Handle plan selection
  const handlePlanToggle = (planId: string) => {
    const newPlans = selectedPlans.includes(planId)
      ? selectedPlans.filter((id) => id !== planId)
      : [...selectedPlans, planId];
    setSelectedPlans(newPlans);
    onChange(audienceType, newPlans, selectedTags, audienceFilter);
  };

  // Handle tag selection
  const handleTagToggle = (tagId: string) => {
    const newTags = selectedTags.includes(tagId)
      ? selectedTags.filter((id) => id !== tagId)
      : [...selectedTags, tagId];
    setSelectedTags(newTags);
    onChange(audienceType, selectedPlans, newTags, audienceFilter);
  };

  // Calculate estimated audience
  const getEstimatedAudience = () => {
    const safePlans = plans ?? [];
    const safeTags = tags ?? [];
    if (audienceType === 'all') {
      return safePlans.reduce((sum, p) => sum + p.subscriberCount, 0);
    }
    if (audienceType === 'plans') {
      return safePlans
        .filter((p) => selectedPlans.includes(p.id))
        .reduce((sum, p) => sum + p.subscriberCount, 0);
    }
    if (audienceType === 'tags') {
      return safeTags
        .filter((t) => selectedTags.includes(t.id))
        .reduce((sum, t) => sum + t.accountCount, 0);
    }
    return 0;
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Audience Type Selection */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Target Audience</Label>
        <RadioGroup
          value={audienceType}
          onValueChange={(val) => handleTypeChange(val as CampaignAudienceType)}
          className="grid gap-3 sm:grid-cols-2"
        >
          {audienceOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = audienceType === option.value;

            return (
              <label
                key={option.value}
                className={cn(
                  'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all',
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                )}
              >
                <RadioGroupItem value={option.value} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className={cn(
                      'h-4 w-4',
                      isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500'
                    )} />
                    <span className={cn(
                      'font-medium',
                      isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-900 dark:text-white'
                    )}>
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    {option.description}
                  </p>
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      {/* Plan Selection */}
      {audienceType === 'plans' && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">Select Plans</Label>
          <div className="border rounded-lg divide-y dark:border-slate-700 dark:divide-slate-700 max-h-64 overflow-y-auto">
            {isLoadingPlans ? (
              <div className="p-4 text-center text-sm text-slate-500">
                Loading plans...
              </div>
            ) : plans.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                No plans available
              </div>
            ) : (
              (plans ?? []).filter(p => p.isActive).map((plan) => (
                <label
                  key={plan.id}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedPlans.includes(plan.id)}
                    onCheckedChange={() => handlePlanToggle(plan.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-900 dark:text-white">
                      {plan.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {plan.subscriberCount} accounts
                    </div>
                  </div>
                  {selectedPlans.includes(plan.id) && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tag Selection */}
      {audienceType === 'tags' && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">Select Tags</Label>
          <div className="border rounded-lg divide-y dark:border-slate-700 dark:divide-slate-700 max-h-64 overflow-y-auto">
            {isLoadingTags ? (
              <div className="p-4 text-center text-sm text-slate-500">
                Loading tags...
              </div>
            ) : tags.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                No tags available. Create tags in the Accounts section.
              </div>
            ) : (
              tags.map((tag) => (
                <label
                  key={tag.id}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedTags.includes(tag.id)}
                    onCheckedChange={() => handleTagToggle(tag.id)}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="font-medium text-sm text-slate-900 dark:text-white">
                      {tag.name}
                    </span>
                    <span className="text-xs text-slate-500">
                      {tag.accountCount} accounts
                    </span>
                  </div>
                  {selectedTags.includes(tag.id) && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {/* Custom Filter (placeholder) */}
      {audienceType === 'custom' && (
        <div className="border rounded-lg p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
            Custom filter builder will be implemented with advanced query capabilities.
          </p>
        </div>
      )}

      {/* Estimated Audience */}
      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
          <Users className="h-5 w-5" />
          <span>Estimated audience</span>
        </div>
        <span className="text-lg font-semibold text-slate-900 dark:text-white tabular-nums">
          {getEstimatedAudience().toLocaleString()} accounts
        </span>
      </div>
    </div>
  );
}
